import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { PlayerProfile, Achievement, GameSettings, GameStatus } from '@/types/games';

const STORAGE_KEY = 'clinique-games-profile';

const DEFAULT_ACHIEVEMENTS: Achievement[] = [
  { id: 'first_game', name: 'Premier pas', description: 'Jouer à votre premier jeu', icon: 'ri-gamepad-line', unlockedAt: null },
  { id: 'ten_games', name: 'Joueur régulier', description: 'Jouer 10 parties', icon: 'ri-star-line', unlockedAt: null },
  { id: 'fifty_games', name: 'Passionné', description: 'Jouer 50 parties', icon: 'ri-star-fill', unlockedAt: null },
  { id: 'hundred_games', name: 'Inarrêtable', description: 'Jouer 100 parties', icon: 'ri-trophy-line', unlockedAt: null },
  { id: 'perfect_quiz', name: 'Sans faute', description: 'Réussir un quiz sans erreur', icon: 'ri-check-double-line', unlockedAt: null },
  { id: 'speed_demon', name: 'Éclair', description: 'Terminer un jeu en moins de 30 secondes', icon: 'ri-flashlight-line', unlockedAt: null },
  { id: 'anatomie_master', name: 'Expert Anatomie', description: 'Placer tous les organes sans erreur', icon: 'ri-heart-pulse-line', unlockedAt: null },
  { id: 'memory_king', name: 'Roi de la Mémoire', description: 'Trouver toutes les paires en moins de 10 coups', icon: 'ri-brain-line', unlockedAt: null },
  { id: 'snake_master', name: 'Nutritionniste', description: 'Atteindre un score de 100 au Snake', icon: 'ri-apple-line', unlockedAt: null },
  { id: 'crossword_pro', name: 'Cruciverbiste', description: 'Compléter une grille de mots croisés', icon: 'ri-file-text-line', unlockedAt: null },
  { id: 'sudoku_pro', name: 'Sudoka', description: 'Résoudre un Sudoku difficile', icon: 'ri-grid-line', unlockedAt: null },
  { id: 'pendu_10', name: 'Linguiste', description: 'Deviner 10 mots au Pendu', icon: 'ri-translate-2', unlockedAt: null },
];

const DEFAULT_PROFILE: PlayerProfile = {
  gamesPlayed: 0,
  totalTimeSeconds: 0,
  totalScore: 0,
  achievements: DEFAULT_ACHIEVEMENTS,
  bestScores: {},
  lastPlayed: {},
};

interface GameContextType {
  profile: PlayerProfile;
  settings: GameSettings;
  currentGame: string | null;
  gameStatus: GameStatus;
  gameScore: number;
  gameTime: number;
  gameErrors: number;
  gameLevel: number;
  // Actions
  startGame: (gameId: string) => void;
  endGame: (score: number, time: number, errors: number) => void;
  updateScore: (score: number) => void;
  updateErrors: (count: number) => void;
  updateLevel: (level: number) => void;
  pauseGame: () => void;
  resumeGame: () => void;
  quitGame: () => void;
  unlockAchievement: (achievementId: string) => void;
  updateSettings: (newSettings: Partial<GameSettings>) => void;
  resetProfile: () => void;
  formatTime: (seconds: number) => string;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

function loadProfile(): PlayerProfile {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_PROFILE, ...parsed };
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_PROFILE, achievements: [...DEFAULT_ACHIEVEMENTS] };
}

function saveProfile(profile: PlayerProfile) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    // ignore
  }
}

export function GameProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<PlayerProfile>(loadProfile);
  const [settings, setSettings] = useState<GameSettings>({
    musicEnabled: false,
    soundEffectsEnabled: true,
    fullscreen: false,
    showTimer: true,
  });
  const [currentGame, setCurrentGame] = useState<string | null>(null);
  const [gameStatus, setGameStatus] = useState<GameStatus>('idle');
  const [gameScore, setGameScore] = useState(0);
  const [gameTime, setGameTime] = useState(0);
  const [gameErrors, setGameErrors] = useState(0);
  const [gameLevel, setGameLevel] = useState(1);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (gameStatus === 'playing' && settings.showTimer) {
      timerRef.current = setInterval(() => {
        setGameTime((t) => t + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [gameStatus, settings.showTimer]);

  const startGame = useCallback((gameId: string) => {
    setCurrentGame(gameId);
    setGameStatus('playing');
    setGameScore(0);
    setGameTime(0);
    setGameErrors(0);
    setGameLevel(1);
  }, []);

  const endGame = useCallback((score: number, time: number, errors: number) => {
    setGameStatus(score > 0 ? 'won' : 'lost');
    setGameScore(score);
    setGameTime(time);
    setGameErrors(errors);

    const updated = { ...profile };
    updated.gamesPlayed += 1;
    updated.totalTimeSeconds += time;
    updated.totalScore += score;
    const prev = updated.bestScores[currentGame || ''] || 0;
    if (score > prev) {
      updated.bestScores[currentGame || ''] = score;
    }
    updated.lastPlayed[currentGame || ''] = new Date().toISOString();

    // Check achievements
    if (updated.gamesPlayed >= 1 && !updated.achievements.find(a => a.id === 'first_game')?.unlockedAt) {
      const a = updated.achievements.find(a => a.id === 'first_game');
      if (a) a.unlockedAt = new Date().toISOString();
    }
    if (updated.gamesPlayed >= 10 && !updated.achievements.find(a => a.id === 'ten_games')?.unlockedAt) {
      const a = updated.achievements.find(a => a.id === 'ten_games');
      if (a) a.unlockedAt = new Date().toISOString();
    }
    if (updated.gamesPlayed >= 50 && !updated.achievements.find(a => a.id === 'fifty_games')?.unlockedAt) {
      const a = updated.achievements.find(a => a.id === 'fifty_games');
      if (a) a.unlockedAt = new Date().toISOString();
    }
    if (updated.gamesPlayed >= 100 && !updated.achievements.find(a => a.id === 'hundred_games')?.unlockedAt) {
      const a = updated.achievements.find(a => a.id === 'hundred_games');
      if (a) a.unlockedAt = new Date().toISOString();
    }
    if (errors === 0 && !updated.achievements.find(a => a.id === 'perfect_quiz')?.unlockedAt) {
      const a = updated.achievements.find(a => a.id === 'perfect_quiz');
      if (a) a.unlockedAt = new Date().toISOString();
    }
    if (time < 30 && !updated.achievements.find(a => a.id === 'speed_demon')?.unlockedAt) {
      const a = updated.achievements.find(a => a.id === 'speed_demon');
      if (a) a.unlockedAt = new Date().toISOString();
    }

    setProfile(updated);
    saveProfile(updated);
  }, [profile, currentGame]);

  const updateScore = useCallback((score: number) => setGameScore(score), []);
  const updateErrors = useCallback((count: number) => setGameErrors(count), []);
  const updateLevel = useCallback((level: number) => setGameLevel(level), []);
  const pauseGame = useCallback(() => setGameStatus('paused'), []);
  const resumeGame = useCallback(() => setGameStatus('playing'), []);

  const quitGame = useCallback(() => {
    setCurrentGame(null);
    setGameStatus('idle');
    setGameScore(0);
    setGameTime(0);
    setGameErrors(0);
    setGameLevel(1);
  }, []);

  const unlockAchievement = useCallback((achievementId: string) => {
    setProfile((prev) => {
      const updated = { ...prev, achievements: prev.achievements.map((a) => (a.id === achievementId && !a.unlockedAt ? { ...a, unlockedAt: new Date().toISOString() } : a)) };
      saveProfile(updated);
      return updated;
    });
  }, []);

  const updateSettings = useCallback((newSettings: Partial<GameSettings>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  }, []);

  const resetProfile = useCallback(() => {
    const fresh = { ...DEFAULT_PROFILE, achievements: DEFAULT_ACHIEVEMENTS.map((a) => ({ ...a, unlockedAt: null })) };
    setProfile(fresh);
    saveProfile(fresh);
  }, []);

  const formatTime = useCallback((seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }, []);

  return (
    <GameContext.Provider
      value={{
        profile, settings, currentGame, gameStatus, gameScore, gameTime, gameErrors, gameLevel,
        startGame, endGame, updateScore, updateErrors, updateLevel, pauseGame, resumeGame, quitGame,
        unlockAchievement, updateSettings, resetProfile, formatTime,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

export function useGame(): GameContextType {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used within GameProvider');
  return ctx;
}